import {z} from 'zod';
import {buildTsplPlan,type LabelContent,type LabelField,type LabelSize} from './label';

export const DEVICE_KINDS=['Scan','Scale','Print'] as const;
export type DeviceKind=(typeof DEVICE_KINDS)[number];
export const DEVICE_CHANNELS=['Simulator','Hardware'] as const;
export type DeviceChannel=(typeof DEVICE_CHANNELS)[number];
export const SCALE_UNITS=['g','kg','lb','oz'] as const;
export type ScaleUnit=(typeof SCALE_UNITS)[number];

const LOT_RE=/^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/;
const PRODUCT_RE=/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;

export type ParsedBarcode=
 | {kind:'lot';lotNumber:string;canonical:string}
 | {kind:'productLot';productCode:string;lotNumber:string;canonical:string};

export function parseBarcode(raw:string):ParsedBarcode{
 const text=raw.replace(/^\uFEFF/,'').trim();
 if(!text)throw new Error('device_parse');
 // eslint-disable-next-line no-control-regex -- CỐ Ý: lọc bỏ ký tự điều khiển trước khi gửi ra máy in tem / chứng từ. Đó chính là việc của dòng này.
 if(/[\x00-\x1F\x7F]/.test(text))throw new Error('device_parse');
 if(text.length>80)throw new Error('device_parse');
 const lotOnly=text.match(/^LOT:(.+)$/i);
 if(lotOnly){
  const lotNumber=lotOnly[1]!.trim();
  if(!LOT_RE.test(lotNumber))throw new Error('device_parse');
  return {kind:'lot',lotNumber,canonical:`LOT:${lotNumber}`};
 }
 const productLot=text.match(/^HISENSE:([^:]+):(.+)$/i);
 if(productLot){
  const productCode=productLot[1]!.trim();
  const lotNumber=productLot[2]!.trim();
  if(!PRODUCT_RE.test(productCode)||!LOT_RE.test(lotNumber))throw new Error('device_parse');
  return {kind:'productLot',productCode,lotNumber,canonical:`HISENSE:${productCode}:${lotNumber}`};
 }
 const gs1=text.match(/^01(\d{14})10([A-Za-z0-9._/-]{1,20})$/);
 if(gs1)return {kind:'lot',lotNumber:gs1[2]!,canonical:`LOT:${gs1[2]}`};
 throw new Error('device_parse');
}

export interface ParsedScale {grams:number;unit:ScaleUnit;canonical:string;}
const SCALE_BODY=/^(?:ST,GS,)?\s*([+-]?)(?:\s*)(\d{1,8}(?:\.\d{1,4})?)\s*(g|kg|lb|oz)\s*$/i;

export function parseScale(raw:string):ParsedScale{
 const text=raw.replace(/^\uFEFF/,'').trim();
 if(!text)throw new Error('device_parse');
 // eslint-disable-next-line no-control-regex -- CỐ Ý: lọc bỏ ký tự điều khiển trước khi gửi ra máy in tem / chứng từ. Đó chính là việc của dòng này.
 if(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(text))throw new Error('device_parse');
 const match=text.match(SCALE_BODY);
 if(!match)throw new Error('device_parse');
 if(match[1]==='-')throw new Error('device_parse');
 const amount=Number(match[2]);
 if(!Number.isFinite(amount)||amount<=0)throw new Error('device_parse');
 const unit=match[3]!.toLowerCase() as ScaleUnit;
 const grams=unit==='g'?amount:unit==='kg'?amount*1000:unit==='lb'?amount*453.59237:amount*28.349523125;
 if(!(grams>0)||grams>1_000_000_000)throw new Error('device_parse');
 return {grams,unit,canonical:`${amount} ${unit}`};
}

export interface ScaleCalibration {offsetGrams:number;factor:number;}
export function applyCalibration(grams:number,calibration:ScaleCalibration|null):{grams:number;calibrated:boolean}{
 if(!calibration)return {grams,calibrated:false};
 if(!Number.isFinite(calibration.offsetGrams)||!Number.isFinite(calibration.factor)||calibration.factor===0)throw new Error('device_calibration');
 const next=(grams+calibration.offsetGrams)*calibration.factor;
 if(!(next>0)||!Number.isFinite(next))throw new Error('device_calibration');
 return {grams:next,calibrated:true};
}

export function fingerprint(text:string):string{
 let h=2166136261;
 for(let i=0;i<text.length;i++){h^=text.charCodeAt(i);h=Math.imul(h,16777619);}
 return (h>>>0).toString(16).padStart(8,'0');
}

export function simulatePrint(size:LabelSize,content:Omit<LabelContent,'count'>&{count?:number}):{channel:'Simulator';hardwareVerified:false;payload:string;omitted:LabelField[]}{
 const plan=buildTsplPlan(size,{...content,count:content.count??1});
 return {channel:'Simulator',hardwareVerified:false,payload:plan.payload,omitted:plan.omitted};
}

export const deviceChannelSchema=z.enum(DEVICE_CHANNELS);
export const scanDraftSchema=z.object({requestId:z.string().uuid(),channel:deviceChannelSchema.default('Simulator'),raw:z.string().min(1).max(120),timeoutMs:z.number().int().min(1).max(30000).optional()});
export const scaleDraftSchema=z.object({
 requestId:z.string().uuid(),channel:deviceChannelSchema.default('Simulator'),raw:z.string().min(1).max(80),
 offsetGrams:z.number().finite().optional(),factor:z.number().finite().optional(),timeoutMs:z.number().int().min(1).max(30000).optional(),
});
export const printDraftSchema=z.object({
 requestId:z.string().uuid(),channel:deviceChannelSchema.default('Simulator'),
 size:z.object({presetKey:z.string().min(1).max(20),widthMm:z.number().positive(),heightMm:z.number().positive()}),
 content:z.object({productRef:z.string().min(1).max(80),description:z.string().max(200).optional(),color:z.string().max(80).optional(),size:z.string().max(40).optional(),sampleQty:z.string().max(40).optional(),lotNumber:z.string().max(80).optional(),barcode:z.string().max(40).nullable().optional(),count:z.number().int().positive().max(99).optional()}),
});
export type ScanDraftInput=z.input<typeof scanDraftSchema>;
export type ScaleDraftInput=z.input<typeof scaleDraftSchema>;
export type PrintDraftInput=z.infer<typeof printDraftSchema>;

export interface DeviceEventDto {
 id:string;requestId:string;kind:DeviceKind;channel:DeviceChannel;status:'Posted'|'Rejected';
 canonical:string;hardwareVerified:false;errorCode:string|null;createdAt:number;
 lookup?:{lotNumber:string;productCode:string;availableQuantity:string;unit:string;warehouseName:string;location:string}[];
 grams?:number;calibrated?:boolean;payload?:string;
 /** Trường có dữ liệu nhưng không in được vì hết chỗ trên tem. */
 omitted?:LabelField[];
}


